import { MutateOptions, MutationKey, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import { HttpException } from '../exceptions/index';
import { StringKey } from '../lang/index';
import { createIdempotencyKey, idempotencyKeyHeaders } from '../utils/idempotency-key.util';
import { useLocale } from './use-locale.hook';

const FALLBACK_ERROR_MESSAGE = 'Request failed';

type IdempotentMutationFn<TData, TVariables> = (
  variables: TVariables,
  request: { headers: Record<string, string> },
) => Promise<TData>;

type IdempotentAppMutationProps<
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
> = {
  defaultErrorHandling?: boolean;
  mutationFn: IdempotentMutationFn<TData, TVariables>;
} & Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn' | 'mutationKey'>;

export const useIdempotentAppMutation = <
  TData = unknown,
  TError = HttpException,
  TVariables = void,
  TContext = unknown,
>(
  mutationKey: MutationKey = [],
  {
    defaultErrorHandling = true,
    mutationFn,
    ...params
  }: IdempotentAppMutationProps<TData, TError, TVariables, TContext>,
) => {
  const { messagesLocale } = useLocale();
  const inFlightRef = useRef(false);
  const idempotencyKeyRef = useRef<null | string>(null);

  const mutation = useMutation({
    mutationKey,
    ...params,
    mutationFn: (variables) => {
      const key = idempotencyKeyRef.current ?? createIdempotencyKey();
      idempotencyKeyRef.current = null;
      return mutationFn(variables, { headers: idempotencyKeyHeaders(key) });
    },
    onError: (error, variables, context) => {
      if (defaultErrorHandling && error instanceof HttpException) {
        const { body } = error;
        const messageKey =
          typeof body?.message === 'string' ? (body.message as StringKey) : undefined;
        const localizedMessage = messageKey ? messagesLocale?.[messageKey] : undefined;
        const capitalizedLocalizedMessage = localizedMessage
          ? localizedMessage[0]?.toUpperCase() + localizedMessage.slice(1)
          : undefined;
        const normalizedMessage =
          capitalizedLocalizedMessage || body?.message || error.message || FALLBACK_ERROR_MESSAGE;
        toast(`${normalizedMessage}`, {
          position: 'bottom-center',
          type: 'error',
          theme: 'light',
        });
      }

      params.onError?.(error, variables, context);
    },
  });

  const releaseInFlight = useCallback(() => {
    inFlightRef.current = false;
  }, []);

  const beginInFlight = useCallback((): boolean => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    idempotencyKeyRef.current = createIdempotencyKey();
    return true;
  }, []);

  const mutate = useCallback(
    (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => {
      if (!beginInFlight()) return;
      mutation.mutate(variables, {
        ...options,
        onSettled: (...args) => {
          releaseInFlight();
          options?.onSettled?.(...args);
        },
      });
    },
    [beginInFlight, mutation, releaseInFlight],
  );

  const mutateAsync = useCallback(
    async (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => {
      if (!beginInFlight()) {
        throw new Error('Duplicate mutation request ignored');
      }

      try {
        return await mutation.mutateAsync(variables, options);
      } finally {
        releaseInFlight();
      }
    },
    [beginInFlight, mutation, releaseInFlight],
  );

  return { ...mutation, mutate, mutateAsync };
};
